import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { PermissionActions, PermissionModules } from 'types/rolesTypes';
import { ROUTES } from 'routes/routes';
import icons from 'components/darwin-ui/Icon/Icons';
import { DarwinListView } from 'components/darwin-ui';
import useTableControls from 'hooks/useTableControls';
import { useUI } from 'contexts/UiContext';
import { DataCaptureListView } from 'types/dataCaptureTypes';
import { getDataCaptureFormsColumns } from './columns';
import { useGetAllDataCaptureForms } from 'api/services/dataCaptureService';
import { DeleteDataCaptureFormModal } from 'components/ui/dataCapture/DeleteDataCaptureFormModal';
import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { ProcessLifeCycleStatus } from 'constants/process-lifecycle-status.constants';

export type ColumnAction = {
  key: string;
  name?: keyof typeof icons;
  label?: string;
  onClick?: (dataCaptureForm: DataCaptureListView) => void;
};

export const DataCaptureFormsListView = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedCompany } = useUI();

  const [dataCaptureFormToDelete, setDataCaptureFormToDelete] =
    useState<DataCaptureListView>();

  const checkPermission = usePermissionChecker();

  const viewDataCaptureFormPermission = checkPermission(
    PermissionModules.FormsDataCapture,
    PermissionActions.Read
  );
  const updateDataCaptureFormPermission = checkPermission(
    PermissionModules.FormsDataCapture,
    PermissionActions.Update
  );
  const deleteDataCaptureFormPermission = checkPermission(
    PermissionModules.FormsDataCapture,
    PermissionActions.Delete
  );

  // table
  const {
    pageSize,
    currentPage,
    onPageSizeChange,
    setCurrentPage,
    searchQuery,
    setSearchQuery,
    sortOrder,
    onTableChange,
  } = useTableControls();

  const {
    data: dataCaptureFormsData,
    isLoading: isLoadingDataCaptureFormsData,
    isFetching: isFetchingDataCaptureFormsData,
    isPending: isPendingDataCaptureFormsData,
  } = useGetAllDataCaptureForms({
    companyId: selectedCompany || '',
    page: currentPage,
    limit: pageSize,
    query: searchQuery,
    sortOrder:
      sortOrder.sortBy === 'id'
        ? { sortBy: 'form_name', order: 'ASC' }
        : sortOrder,
  });

  const tableData = dataCaptureFormsData?.data ?? [];

  const availableActions: Array<{
    permission: boolean | null;
    action: ColumnAction;
  }> = [
    {
      permission: updateDataCaptureFormPermission,
      action: {
        key: 'edit',
        name: 'edit',
        label: t('edit'),
        onClick: dataCaptureForm =>
          navigate(
            generatePath(ROUTES.DATA.CAPTURE_FORMS.EDIT, {
              id: dataCaptureForm.form_id || '',
              traceability: null,
              processFlow: null,
            })
          ),
      },
    },
    {
      permission: viewDataCaptureFormPermission,
      action: {
        key: 'view',
        name: 'view',
        label: t('view'),
        onClick: dataCaptureForm =>
          navigate(
            generatePath(ROUTES.DATA.CAPTURE_FORMS.VIEW, {
              id: dataCaptureForm.form_id,
            })
          ),
      },
    },
    {
      permission: deleteDataCaptureFormPermission,
      action: {
        key: 'delete',
        name: 'delete',
        label: t('delete'),
        onClick: setDataCaptureFormToDelete,
      },
    },
  ];

  const actions: ColumnAction[] = availableActions
    .filter(item => Boolean(item.permission))
    .map(item => item.action);

  const columns = getDataCaptureFormsColumns(t, actions, searchQuery);

  const isLoading =
    isLoadingDataCaptureFormsData ||
    isFetchingDataCaptureFormsData ||
    isPendingDataCaptureFormsData;

  const handleRowClick = useCallback(
    (dataCaptureForm: DataCaptureListView) => {
      if (viewDataCaptureFormPermission) {
        navigate(
          generatePath(ROUTES.DATA.CAPTURE_FORMS.EDIT, {
            id: dataCaptureForm.form_id || '',
            traceability: null,
            processFlow: null,
          })
        );
      }
    },
    [viewDataCaptureFormPermission, navigate]
  );

  return (
    <>
      <ContentHeader title={t('data-capture-list-view.title')} />

      <DarwinListView<DataCaptureListView>
        data={tableData}
        columns={columns}
        isLoading={isLoading}
        pagination={{
          total: dataCaptureFormsData?.metadata?.total,
          pageSize,
          currentPage,
          onPageChange: setCurrentPage,
          onPageSizeChange,
        }}
        header={{
          setSearchQuery,
          searchPlaceholder: t('data-capture-list-view.placeholder'),
        }}
        table={{
          onChange: onTableChange,
        }}
        behavior={{
          isRowInactive: dataCaptureForm =>
            dataCaptureForm.status === ProcessLifeCycleStatus.INACTIVE,
          onRowClick: handleRowClick,
        }}
      />

      <DeleteDataCaptureFormModal
        open={!!dataCaptureFormToDelete}
        onCancel={() => setDataCaptureFormToDelete(undefined)}
        dataCaptureFormId={dataCaptureFormToDelete?.form_id.toString() || ''}
      />
    </>
  );
};
